import React, { useState, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';
import { notificationApiConfig } from '../../graphql/notification-api-config';

export default function useNotificationMenu() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userId, setUserId] = useState(null);
  const [fullUserId, setFullUserId] = useState(null);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const username = user.username;
        const fullId = user.attributes?.sub ? `${user.attributes.sub}::${username}` : username;
        
        setUserId(username);
        setFullUserId(fullId);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    
    getCurrentUser();
  }, []);

  // Subscribe to new transcription notifications
  useEffect(() => {
    if (!fullUserId) return;
    
    let subscriptionRef = null;
    let pollTimeoutRef = null;
    
    const setupSubscription = async () => {
      try {
        // Try real-time subscription first
        const subscriptionQuery = `
          subscription OnTranscriptionComplete($userId: String!) {
            onTranscriptionComplete(userId: $userId) {
              id
              userId
              status
              filename
              message
              errorDetails
              createdAt
            }
          }
        `;
        
        subscriptionRef = API.graphql({
          query: subscriptionQuery,
          variables: { userId: fullUserId },
          authMode: 'API_KEY',
          apiKey: notificationApiConfig.apiKey,
          endpoint: notificationApiConfig.url
        }).subscribe({
          next: (data) => {
            if (data && data.data && data.data.onTranscriptionComplete) {
              const notification = data.data.onTranscriptionComplete;
              addNewNotification(notification);
            }
          },
          error: (error) => {
            // Fall back to polling
            setupPolling();
          }
        });
        
      } catch (error) {
        // Fall back to polling
        setupPolling();
      }
    };
    
    const setupPolling = () => {
      let pollInterval = 3000; // 3 seconds
      const maxPollInterval = 10000; // Max 10 seconds
      let consecutiveEmptyPolls = 0;
      const shownNotificationIds = new Set();
      
      const pollForNotifications = async () => {
        try {
          const response = await fetch(notificationApiConfig.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': notificationApiConfig.apiKey,
            },
            body: JSON.stringify({
              query: `
                query GetNotificationsByUser($userId: String!) {
                  getNotificationsByUser(userId: $userId) {
                    id
                    userId
                    status
                    filename
                    message
                    errorDetails
                    createdAt
                  }
                }
              `,
              variables: { userId: fullUserId }
            })
          });
          
          const result = await response.json();
          
          if (result.data && result.data.getNotificationsByUser) {
            const backendNotifications = result.data.getNotificationsByUser;
            
            // Check for new notifications
            const newNotifications = backendNotifications.filter(
              notification => !shownNotificationIds.has(notification.id)
            );
            
            if (newNotifications.length > 0) {
              consecutiveEmptyPolls = 0;
              pollInterval = 3000; // Reset to fast polling
              
              // Add new notifications
              newNotifications.forEach(notification => {
                shownNotificationIds.add(notification.id);
                addNewNotification(notification);
              });
            } else {
              consecutiveEmptyPolls++;
              if (consecutiveEmptyPolls > 2) {
                pollInterval = Math.min(pollInterval * 1.5, maxPollInterval);
              }
            }
          }
        } catch (error) {
          pollInterval = Math.min(pollInterval * 2, maxPollInterval);
        }
        
        // Schedule next poll
        pollTimeoutRef = setTimeout(pollForNotifications, pollInterval);
      };
      
      // Start polling
      pollForNotifications();
    };
    
    // Try subscription first
    setupSubscription();
    
    return () => {
      if (subscriptionRef) {
        subscriptionRef.unsubscribe();
      }
      if (pollTimeoutRef) {
        clearTimeout(pollTimeoutRef);
      }
    };
  }, [fullUserId]);

  // Load initial notifications on mount
  useEffect(() => {
    if (!fullUserId) return;
    
    const fetchInitialNotifications = async () => {
      try {
        const response = await fetch(notificationApiConfig.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': notificationApiConfig.apiKey,
          },
          body: JSON.stringify({
            query: `
              query GetNotificationsByUser($userId: String!) {
                getNotificationsByUser(userId: $userId) {
                  id
                  userId
                  status
                  filename
                  message
                  errorDetails
                  createdAt
                }
              }
            `,
            variables: { userId: fullUserId }
          })
        });
        
        const result = await response.json();
        
        if (result.data && result.data.getNotificationsByUser) {
          const backendNotifications = result.data.getNotificationsByUser;
          
          const formattedNotifications = backendNotifications.map(notification => ({
            id: notification.id,
            message: notification.message,
            type: notification.status,
            filename: notification.filename,
            errorDetails: notification.errorDetails,
            createdAt: notification.createdAt,
            isRead: false // All notifications start as unread
          }));
          
          setNotifications(formattedNotifications);
          setUnreadCount(formattedNotifications.length);
        }
      } catch (error) {
        console.error("Error fetching initial notifications:", error);
      }
    };
    
    fetchInitialNotifications();
  }, [fullUserId]);

  const addNewNotification = (notification) => {
    const formattedNotification = {
      id: notification.id,
      message: notification.message,
      type: notification.status,
      filename: notification.filename,
      errorDetails: notification.errorDetails,
      createdAt: notification.createdAt,
      isRead: false
    };
    
    setNotifications(prev => {
      // Avoid duplicates
      if (prev.some(n => n.id === notification.id)) {
        return prev;
      }
      return [formattedNotification, ...prev];
    });
    
    setUnreadCount(count => count + 1);
  };

  // Set up event listener for notification dismissals from other components
  useEffect(() => {
    const handleNotificationDismissed = (event) => {
      const { notificationId } = event.detail;
      console.log('Notification dismissed event received:', notificationId);
      
      // Remove the notification from local state
      setNotifications(prev => {
        const wasUnread = prev.find(n => n.id === notificationId)?.isRead === false;
        const filtered = prev.filter(n => n.id !== notificationId);
        
        // Update unread count if the dismissed notification was unread
        if (wasUnread) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        
        return filtered;
      });
    };
    
    // Listen for custom notification dismissed events
    window.addEventListener('notificationDismissed', handleNotificationDismissed);
    
    return () => {
      window.removeEventListener('notificationDismissed', handleNotificationDismissed);
    };
  }, []);

  const markAsRead = async (id) => {
    try {
      const response = await fetch(notificationApiConfig.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': notificationApiConfig.apiKey,
        },
        body: JSON.stringify({
          query: `
            mutation MarkNotificationAsRead($id: ID!) {
              markNotificationAsRead(id: $id) {
                id
                isRead
              }
            }
          `,
          variables: { id }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('Error marking notification as read:', result.errors);
      } else {
        console.log('Notification marked as read:', result.data.markNotificationAsRead);
        
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(count => Math.max(0, count - 1));
      }
    } catch (error) {
      console.error('Error calling markNotificationAsRead:', error);
    }
  };

  const deleteNotif = async (id) => {
    // For now, we'll just mark as read instead of deleting
    await markAsRead(id);
    
    // Remove from local state
    const wasUnread = notifications.find(n => n.id === id)?.isRead === false;
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (wasUnread) {
      setUnreadCount(count => Math.max(0, count - 1));
    }
  };

  // Generate notification items for the dropdown
  const getNotificationItems = () => {
    if (notifications.length === 0) {
      return [{ text: 'No notifications', disabled: true }];
    }

    return notifications.map(notification => {
      const formattedDate = new Date(notification.createdAt).toLocaleString();
      const isSuccess = notification.type === 'SUCCESS';
      
      // Create description with error details for failed transcriptions
      let description = `${notification.filename} (${formattedDate})`;
      if (!isSuccess && notification.errorDetails) {
        description += ` - Error: ${notification.errorDetails}`;
      }
      
      return {
        text: notification.message,
        description: description,
        id: notification.id,
        info: isSuccess ? 'success' : 'error',
        dismissLabel: 'Dismiss',
        onItemClick: () => markAsRead(notification.id),
        onDismiss: () => deleteNotif(notification.id)
      };
    });
  };

  // Return the notification utility object for TopNavigation
  return {
    type: 'menu-dropdown',
    text: 'Notifications',
    iconName: 'notification',
    badge: unreadCount > 0 ? { content: unreadCount } : undefined,
    items: getNotificationItems(),
    onItemClick: (e) => {
      if (e.detail.id) {
        markAsRead(e.detail.id);
      }
    }
  };
}
